import {
  createElement,
  useRef,
  useState,
  KeyboardEvent,
  useMemo,
  forwardRef,
  AriaAttributes,
  CSSProperties,
} from 'react';
import { ButtonVariant, ButtonProps, getButtonDesignTokens, ButtonTokens } from '../../Button';
import { ArrowSmallDownBtn } from '@sas/react-svg';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { ItemLocation, Menu, MenuItem } from '../../Menu';
import { InvisibleText } from '../../InvisibleText';
import { useDesignTokens } from '../../../theme';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import bundle from '../NovaComponents-SplitButton-gui-icu.properties';
import { BaseButton } from '../../Button/__internal__/BaseButton';
import { useToggleMenu } from '../../MenuButton/useToggleMenu';
import { BaseMenuProps } from '../../Menu/__internal__';
import { setRef } from '../../../util/ref-util';
import { postfixTestId, TestIdProps } from '../../../util/testId';
import cx from 'classnames';
import cn from './BaseSplitButton.css.json';
import './BaseSplitButton.css';

const useHtmlId = createUseHtmlId('SplitButton');
const useTextLocalizer = createUseTextLocalizer(bundle);

type MenuAlign = Pick<BaseMenuProps['position'], 'verticalAlign' | 'horizontalAlign'>;

export interface SplitButtonTokens extends ButtonTokens {
  fontFamily?: string;
}

/** @internal */
export interface BaseSplitButtonProps
  extends Pick<ButtonProps, 'className' | 'style' | 'disabled' | 'onPress' | 'text'>,
    TestIdProps {
  items: ReadonlyArray<string | Pick<MenuItem, 'text' | 'disabled'>>;
  onItemSelected: (index: number) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onFocus?: () => void;
  updateButtonAction?: boolean;
  menuAlign?: MenuAlign;
  variant?: null | ButtonVariant;
  ariaDescribedBy?: string | string[];
  focusable?: boolean;
  tokens?: SplitButtonTokens;
  /**
   * When prop set, data-autofocus attribute will be set on element.
   * This will be effective only when component is inside react focus lock.
   */
  autoFocus?: boolean;

  /** @internal */
  ariaCurrent?: AriaAttributes['aria-current'];
  /** @internal */
  ariaRoleDescription?: string;
  /** @internal */
  buttonStyle?: CSSProperties;
  /** @internal */
  buttonClassName?: string;
}

/** @internal */
export const BaseSplitButton = forwardRef<HTMLDivElement, BaseSplitButtonProps>(
  function SplitButton(
    {
      disabled,
      onItemSelected,
      onPress,
      variant,
      tokens,
      items,
      text,
      className,
      style,
      updateButtonAction = false,
      menuAlign,
      testId,
      focusable = true,
      autoFocus,
      ...props
    },
    ref
  ) {
    const v = variant || ButtonVariant.Standard;
    const [defaultFocusIndex, setDefaultFocusIndex] = useState(-1);
    const [activeItemIndex, setActiveItemIndex] = useState<number | undefined>();
    const [isActive, setIsActive] = useState(false);
    const localize = useTextLocalizer();

    const selfRef = useRef<HTMLDivElement | null>(null);
    const dt = useDesignTokens();
    const buttonTokens = useMemo(
      () => getButtonDesignTokens({ disabled, dt, variant: v, tokens }),
      [disabled, dt, v, tokens]
    );

    const {
      handleDismiss,
      handleMouseDown: handleMenuButtonMouseDown,
      handlePress: handleMenuButtonPress,
      setMenuIsOpen,
      menuIsOpen,
    } = useToggleMenu({
      disabled,
      onDismiss: () => {
        selfRef.current?.focus();
        props.onClose?.();
      },
      onOpen: () => {
        setDefaultFocusIndex(-1);
        props.onOpen?.();
      },
    });

    if (updateButtonAction && activeItemIndex !== undefined) {
      const activeItem = items[activeItemIndex];
      text = typeof activeItem === 'string' ? activeItem : activeItem.text;
    }

    const handleKeyDown = function (e: KeyboardEvent<HTMLDivElement>) {
      if (!disabled && !menuIsOpen) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsActive(true);
        }

        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          setMenuIsOpen(true);
          props.onOpen?.();
          setDefaultFocusIndex(e.key === 'ArrowDown' ? 0 : items.length - 1);
        }
      }
    };

    const handleKeyUp = function (e: KeyboardEvent<HTMLDivElement>) {
      if (!disabled && !menuIsOpen) {
        if ((e.key === 'Enter' || e.key === ' ') && isActive) {
          setIsActive(false);
          handleDefaultButtonPress();
        }
      }
    };

    const hanldeItemSelected = function (location: ItemLocation[]) {
      const index = location[0][1];
      onItemSelected(index);
      setMenuIsOpen(false);
      props.onClose?.();
      if (updateButtonAction) {
        setActiveItemIndex(index);
      }
      selfRef.current?.focus();
    };

    const handleDefaultButtonPress = function () {
      if (updateButtonAction && activeItemIndex !== undefined) {
        onItemSelected(activeItemIndex);
      } else {
        onPress?.();
      }
    };

    const invisibleTextId = useHtmlId();
    const ariaDescribedBy: string[] = [];
    if (!disabled) {
      ariaDescribedBy.push(invisibleTextId);
    }
    if (props.ariaDescribedBy) {
      if (typeof props.ariaDescribedBy === 'string') {
        ariaDescribedBy.push(props.ariaDescribedBy);
      } else {
        ariaDescribedBy.push(...props.ariaDescribedBy);
      }
    }

    return (
      <div
        ref={(element) => {
          selfRef.current = element;
          setRef(ref, element);
        }}
        role="group"
        aria-roledescription={
          props.ariaRoleDescription === ''
            ? undefined
            : props.ariaRoleDescription ?? localize('role.aria')
        }
        aria-expanded={menuIsOpen}
        aria-haspopup="menu"
        aria-label={text}
        aria-describedby={ariaDescribedBy.join(' ')}
        aria-disabled={disabled}
        aria-current={props.ariaCurrent}
        className={cx(cn.container, className)}
        data-testid={testId}
        tabIndex={focusable ? 0 : -1}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        style={{ ...buttonTokens, ...style }}
        onBlur={() => setIsActive(false)}
        data-autofocus={autoFocus}
        onFocus={props.onFocus}
      >
        {!disabled ? (
          <InvisibleText id={invisibleTextId}>{localize('button.aria')}</InvisibleText>
        ) : null}
        <BaseButton
          className={cx(cn.button, !disabled && isActive && cn.active, props.buttonClassName)}
          focusable={false}
          disabled={disabled}
          onPress={handleDefaultButtonPress}
          text={text}
          variant={variant}
          // suppress the button tokens, use the buttonTokens above.
          tokens={() => ({
            fg: undefined,
            bg: undefined,
            bd: undefined,
            opacity: undefined,
            filter: undefined,
          })}
          style={props.buttonStyle}
          testId={postfixTestId(testId, 'actionButton')}
        />
        <BaseButton
          className={cx(cn.menu, !disabled && menuIsOpen && cn.active)}
          icon={{ icon: ArrowSmallDownBtn, tooltip: '' }}
          focusable={false}
          disabled={disabled}
          variant={variant}
          onPress={handleMenuButtonPress}
          onMouseDown={handleMenuButtonMouseDown}
          // suppress the button tokens, use the buttonTokens above.
          tokens={() => ({
            fg:
              menuIsOpen && variant === ButtonVariant.Primary && dt.themeVariant === 'high-contrast'
                ? dt.c.colorTextInverse
                : !dt.enableNovaTokens && menuIsOpen && dt.themeVariant === 'high-contrast'
                ? dt.stateStandard.active.fg
                : undefined,
            bg: undefined,
            bd: undefined,
            opacity: undefined,
            filter: undefined,
          })}
          ariaLabel={localize('menubutton.aria')}
          testId={postfixTestId(testId, 'menuButton')}
        />
        <Menu
          isOpen={menuIsOpen}
          sections={[
            {
              items: items.map((item) =>
                typeof item === 'string'
                  ? {
                      text: item,
                    }
                  : item
              ),
            },
          ]}
          position={{ dockingElement: selfRef.current, ...menuAlign }}
          onItemSelected={hanldeItemSelected}
          onDismiss={handleDismiss}
          defaultFocusLocation={[0, defaultFocusIndex]}
          tokens={() => ({ fontFamily: tokens?.({}).fontFamily, fg: tokens?.({}).fg })}
        />
      </div>
    );
  }
);
